import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';

export default function TipSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { user } = useAuth();
  
  const [status, setStatus] = useState('checking');
  const [transaction, setTransaction] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await api.get(`/tips/status/${sessionId}`);
        setTransaction(response.data);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.payment_status === 'expired' || response.data.payment_status === 'failed') {
          setStatus('failed');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking status:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="glass-card p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
            <h1 className="font-heading text-2xl font-bold mb-2">Processing Payment</h1>
            <p className="text-muted-foreground">Please wait while we confirm your tip...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h1 className="font-heading text-2xl font-bold mb-2">Tip Sent!</h1>
            <p className="text-muted-foreground mb-4">
              Your ${transaction?.amount?.toFixed(2)} tip has been sent successfully!
            </p>
            {transaction?.message && (
              <p className="text-sm bg-muted/50 p-3 rounded-xl mb-4">"{transaction.message}"</p>
            )}
            <Link to="/home">
              <Button className="rounded-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Feed
              </Button>
            </Link>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Failed</h1>
            <p className="text-muted-foreground mb-4">
              Unfortunately, your payment could not be processed.
            </p>
            <Link to="/home">
              <Button variant="outline" className="rounded-full">
                Back to Feed
              </Button>
            </Link>
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Pending</h1>
            <p className="text-muted-foreground mb-4">
              Your payment is still being processed. You'll receive a notification once it's complete.
            </p>
            <Link to="/home">
              <Button variant="outline" className="rounded-full">
                Back to Feed
              </Button>
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
            <h1 className="font-heading text-2xl font-bold mb-2">Something Went Wrong</h1>
            <p className="text-muted-foreground mb-4">
              We couldn't verify your payment status. Please check your notifications.
            </p>
            <Link to="/home">
              <Button variant="outline" className="rounded-full">
                Back to Feed
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
